import { useEffect, useMemo, useState, type JSX } from 'react';
import { useEngine, type ScreenId } from '../state/engineStore';
import { PidView } from './pid/PidView';
import { Faceplate } from './faceplate/Faceplate';
import { FloatingWindow } from './shell/Window';
import { AlarmBanner } from './alarms/AlarmBanner';
import { AlarmSummary } from './alarms/AlarmSummary';
import { TrendsScreen } from './trends/TrendsScreen';
import { EventLog } from './eventlog/EventLog';
import { KpiPanel } from './game/KpiPanel';
import { SandboxPanel } from './game/SandboxPanel';
import { SimControls } from './shell/SimControls';
import { ScenarioBar } from './game/ScenarioBar';
import { InstrumentCard } from './faceplate/InstrumentCard';
import { StartMenu } from './game/StartMenu';
import { LessonPanel } from './game/LessonPanel';
import { ShiftReport } from './game/ShiftReport';
import { setHornEnabled } from '../audio/horn';

const SCREENS: { id: ScreenId; label: string; key: string }[] = [
  { id: 'overview', label: 'Process', key: 'F1' },
  { id: 'alarms', label: 'Alarms', key: 'F2' },
  { id: 'trends', label: 'Trends', key: 'F3' },
  { id: 'events', label: 'Events', key: 'F4' },
  { id: 'kpi', label: 'Shift report', key: 'F5' },
  { id: 'sandbox', label: 'Sandbox', key: 'F6' },
];

export function App(): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const [lessonOpen, setLessonOpen] = useState(true);
  const [reportOpen, setReportOpen] = useState(true);
  const ready = useEngine((s) => s.ready);
  const error = useEngine((s) => s.error);
  const plant = useEngine((s) => s.plant);
  const screen = useEngine((s) => s.screen);
  const setScreen = useEngine((s) => s.setScreen);
  const view = useEngine((s) => s.view);
  const setView = useEngine((s) => s.setView);
  const windows = useEngine((s) => s.windows);
  const openWindow = useEngine((s) => s.openWindow);
  const closeWindow = useEngine((s) => s.closeWindow);
  const moveWindow = useEngine((s) => s.moveWindow);
  const focusWindow = useEngine((s) => s.focusWindow);
  const send = useEngine((s) => s.send);
  const running = useEngine((s) => s.running);
  const play = useEngine((s) => s.play);
  const pause = useEngine((s) => s.pause);
  const setSpeed = useEngine((s) => s.setSpeed);
  const hornMuted = useEngine((s) => s.hornMuted);
  const toggleHorn = useEngine((s) => s.toggleHorn);
  const hasLesson = useEngine((s) => s.snap?.lesson !== null && s.snap?.lesson !== undefined);
  const score = useEngine((s) => s.snap?.score?.total ?? null);
  const shiftComplete = useEngine((s) => s.snap?.scenario?.complete ?? false);

  useEffect(() => setHornEnabled(!hornMuted), [hornMuted]);

  // Keyboard shortcuts. Deliberately close to a real DCS: ack, silence, pause, speed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      const idx = SCREENS.findIndex((s) => s.key === e.key);
      if (idx >= 0) {
        e.preventDefault();
        setScreen(SCREENS[idx]!.id);
        return;
      }
      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (running) pause();
          else play();
          break;
        case '1':
          setSpeed(1);
          break;
        case '2':
          setSpeed(5);
          break;
        case '3':
          setSpeed(20);
          break;
        case '4':
          setSpeed(100);
          break;
        case 'a':
        case 'A':
          if (e.shiftKey) send({ t: 'ackAll' });
          break;
        case 's':
        case 'S':
          if (!e.metaKey && !e.ctrlKey) send({ t: 'silence' });
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, play, pause, setSpeed, setScreen, send]);

  const layoutView = useMemo(
    () => plant?.layout.views.find((v) => v.id === view) ?? plant?.layout.views[0],
    [plant, view],
  );

  if (error) {
    return (
      <div style={{ padding: 30, color: 'var(--pri-emergency)' }}>
        <h2>The simulation could not start</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!ready || !plant) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', gap: 10 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, letterSpacing: 4, marginBottom: 8 }}>SETPOINT</div>
          <div className="muted">Warming the plant up to its design point…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-deep)' }}>
      <div className="narrow-banner">
        <span aria-hidden>🖥️</span>
        <span>
          Setpoint is a control-room board — it is much easier to run on a screen 1280 px or
          wider. Everything works here, but you will be doing a lot of scrolling.
        </span>
      </div>

      <header
        className="hmi-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '5px 10px',
          background: 'var(--panel-2)',
          borderBottom: '1px solid var(--line-dim)',
        }}
      >
        <button
          onClick={() => setMenuOpen(true)}
          style={{ letterSpacing: 2.5, fontSize: 12, fontWeight: 700, background: 'transparent', border: 'none', padding: '2px 4px' }}
          title="Choose a plant or a shift"
        >
          SETPOINT
        </button>
        <button onClick={() => setMenuOpen(true)} style={{ fontSize: 12, background: 'transparent', border: 'none', padding: 0 }} className="muted">
          {plant.name} ▾
        </button>
        <button
          onClick={() => setScreen('kpi')}
          className="muted num"
          style={{ fontSize: 12, background: 'transparent', border: 'none', padding: 0 }}
          title="Shift score so far — open the shift report"
        >
          {score !== null ? `score ${score.toFixed(0)}` : ''}
        </button>
        <span style={{ flex: 1 }} />
        {SCREENS.map((s) => (
          <button key={s.id} className={screen === s.id ? 'on' : ''} onClick={() => setScreen(s.id)} title={s.key}>
            {s.label}
          </button>
        ))}
        {hasLesson && !lessonOpen && (
          <button onClick={() => setLessonOpen(true)} title="Reopen the lesson panel">
            Lesson
          </button>
        )}
        <button
          onClick={toggleHorn}
          className={hornMuted ? '' : 'on'}
          title={hornMuted ? 'Audible alarms are off' : 'Audible alarms are on'}
        >
          {hornMuted ? '🔇' : '🔔'}
        </button>
      </header>

      <AlarmBanner />
      <ScenarioBar />

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
      <main style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {screen === 'overview' && (
          <>
            {plant.layout.views.length > 1 && (
              <div style={{ display: 'flex', gap: 6, padding: '5px 10px', borderBottom: '1px solid var(--line-dim)' }}>
                {plant.layout.views.map((v) => (
                  <button key={v.id} className={view === v.id ? 'on' : ''} onClick={() => setView(v.id)}>
                    {v.name}
                  </button>
                ))}
              </div>
            )}
            <div style={{ flex: 1, minHeight: 0 }}>
              {layoutView && (
                <PidView
                  view={layoutView}
                  onOpenLoop={(id) => openWindow('faceplate', id)}
                  onOpenInstrument={(id) => openWindow('alarmInfo', id)}
                />
              )}
            </div>
          </>
        )}
        {screen === 'alarms' && <AlarmSummary />}
        {screen === 'trends' && <TrendsScreen />}
        {screen === 'events' && <EventLog />}
        {screen === 'kpi' && <KpiPanel />}
        {screen === 'sandbox' && <SandboxPanel />}
      </main>
      {hasLesson && lessonOpen && <LessonPanel onClose={() => setLessonOpen(false)} />}
      </div>

      <SimControls />

      {menuOpen && (
        <StartMenu
          onClose={() => {
            setMenuOpen(false);
            setLessonOpen(true);
            setReportOpen(true);
          }}
        />
      )}
      {shiftComplete && reportOpen && <ShiftReport onClose={() => setReportOpen(false)} />}

      {windows.map((w) => (
        <FloatingWindow
          key={w.id}
          title={w.ref}
          subtitle={w.kind === 'faceplate' ? 'Controller faceplate' : 'Instrument'}
          x={w.x}
          y={w.y}
          z={w.z}
          width={w.kind === 'faceplate' ? 300 : 260}
          onMove={(x, y) => moveWindow(w.id, x, y)}
          onFocus={() => focusWindow(w.id)}
          onClose={() => closeWindow(w.id)}
        >
          {w.kind === 'faceplate' ? <Faceplate loopId={w.ref} /> : <InstrumentCard id={w.ref} />}
        </FloatingWindow>
      ))}
    </div>
  );
}
